import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Avatar,
  Collapse,
  Badge,
  useTheme,
  alpha,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Dashboard,
  Chat,
  VerifiedUser,
  Mail,
  Notifications,
  ConfirmationNumber,
  Schedule,
  Language,
  ChevronLeft,
  ChevronRight,
  Menu as MenuIcon,
  Settings,
  Logout,
  ExpandLess,
  ExpandMore,
  Business,
  People,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { socketService } from '../../services/socket';
import { useTheme as useAppTheme } from '../../contexts/AppThemeProvider';
import { useLayout } from '../../contexts/LayoutContext';
import { useAzureAuth } from '../../contexts/AzureAuthContext';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactElement;
  badge?: number;
  children?: NavItem[];
}

const SidebarLayout: React.FC = () => {
  const theme = useTheme();
  const { mode } = useAppTheme();
  const { preferences, setSidebarMode } = useLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const { account, logout: azureLogout } = useAzureAuth();
  
  const pendingChatRequests = useSelector((state: RootState) => state.chat?.pendingRequests || []);
  const unreadMessages = useSelector((state: RootState) => state.chat?.unreadCount || 0);
  const chatBadgeCount = pendingChatRequests.length + unreadMessages;
  
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const isExpanded = preferences.sidebarMode === 'expanded';
  const isMini = preferences.sidebarMode === 'mini';
  const drawerWidth = isExpanded ? preferences.sidebarWidth : preferences.miniSidebarWidth;

  const navItems: NavItem[] = [
    { path: '/dashboard', label: 'Dashboard', icon: <Dashboard /> },
    { path: '/clients', label: 'Clients', icon: <Business /> },
    { path: '/users', label: 'Users', icon: <People /> },
    { 
      path: '/chat', 
      label: 'Chat', 
      icon: <Chat />,
      badge: chatBadgeCount,
    },
    { path: '/secure-verification', label: 'Verify', icon: <VerifiedUser /> },
    { path: '/secure-messages', label: 'Messages', icon: <Mail /> },
    { path: '/push-notifications', label: 'Push', icon: <Notifications /> },
    { path: '/tickets', label: 'Tickets', icon: <ConfirmationNumber /> },
    { path: '/meetings', label: 'Meetings', icon: <Schedule /> },
    { path: '/portal', label: 'Portal', icon: <Language /> },
  ];

  const handleNavClick = (item: NavItem) => {
    if (item.path === '/portal') {
      socketService.emit('openPortal', (response: { success: boolean; portalUrl?: string; error?: string }) => {
        if (response.success && response.portalUrl) {
          const link = document.createElement('a');
          link.href = response.portalUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      });
    } else if (item.children) {
      setExpandedItems(prev =>
        prev.includes(item.path)
          ? prev.filter(p => p !== item.path)
          : [...prev, item.path]
      );
    } else {
      navigate(item.path);
      setMobileOpen(false);
    }
  };

  const handleLogout = async () => {
    socketService.disconnect();
    dispatch(logout());
    await azureLogout();
  };

  const toggleSidebar = () => {
    if (isMini) {
      setSidebarMode('expanded');
    } else {
      setSidebarMode('mini');
    }
  };

  const drawerContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: isMini ? '0 16px 16px 0' : 0,  // Round right corners only when collapsed
        background: preferences.themePreset === 'classic' 
          ? '#33344a'  // Classic InvaroSoft sidebar color
          : theme.palette.mode === 'dark'
            ? `linear-gradient(180deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`
            : theme.palette.background.paper,
      }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isExpanded ? 'space-between' : 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          minHeight: 64,
        }}
      >
        {isExpanded ? (
          <>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1, 
                flex: 1,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                }
              }}
              onClick={toggleSidebar}
            >
              <img 
                src="/logo192.png" 
                alt="ITCP" 
                style={{ width: 32, height: 32 }}
              />
              <Typography
                variant="h6"
                noWrap
                sx={{
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  // Always use ITCP branding colors
                  ...(preferences.themePreset === 'classic' 
                    ? { color: '#ffffff' }  // White on blue sidebar
                    : {
                        // Use ITCP blue gradient for modern theme too
                        background: `linear-gradient(135deg, #3099c5 0%, #2b3e7d 100%)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }
                  ),
                }}
              >
                IT Control Panel
              </Typography>
            </Box>
            <IconButton 
              onClick={toggleSidebar} 
              size="small"
              sx={{ color: preferences.themePreset === 'classic' ? '#ffffff' : 'inherit' }}
            >
              <ChevronLeft />
            </IconButton>
          </>
        ) : (
          <IconButton 
            onClick={toggleSidebar} 
            sx={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              p: 1,
              color: preferences.themePreset === 'classic' ? '#ffffff' : 'inherit',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
              }
            }}
          >
            <img 
              src="/logo192.png" 
              alt="ITCP" 
              style={{ width: 28, height: 28 }}
            />
            <ChevronRight sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>

      <List sx={{ flex: 1, px: 1, py: 2 }}>
        {navItems.map((item) => {
          const isSelected = location.pathname.startsWith(item.path);
          const hasChildren = item.children && item.children.length > 0;
          const isItemExpanded = expandedItems.includes(item.path);

          return (
            <React.Fragment key={item.path}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <Tooltip title={!isExpanded ? item.label : ''} placement="right">
                  <ListItemButton
                    onClick={() => handleNavClick(item)}
                    selected={isSelected}
                    sx={{
                      borderRadius: '8px',  // Round all corners for items
                      minHeight: 48,
                      justifyContent: isExpanded ? 'initial' : 'center',
                      px: isExpanded ? 2 : 1,
                      backgroundColor: isSelected
                        ? alpha(theme.palette.primary.main, 0.12)
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      },
                      '&.Mui-selected': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.16),
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: isExpanded ? 2 : 'auto',
                        justifyContent: 'center',
                        color: preferences.themePreset === 'classic' 
                          ? (isSelected ? '#45b6e5' : '#bdbdbd')  // Classic colors
                          : (isSelected ? theme.palette.primary.main : theme.palette.text.secondary),
                      }}
                    >
                      {item.badge ? (
                        <Badge badgeContent={item.badge} color="error">
                          {item.icon}
                        </Badge>
                      ) : (
                        item.icon
                      )}
                    </ListItemIcon>
                    {isExpanded && (
                      <>
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontSize: '0.875rem',
                            fontWeight: isSelected ? 600 : 500,
                            color: preferences.themePreset === 'classic' ? '#ffffff' : 'inherit',
                          }}
                        />
                        {hasChildren && (isItemExpanded ? <ExpandLess /> : <ExpandMore />)}
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
              {hasChildren && isItemExpanded && (
                <Collapse in={isItemExpanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 4 }}>
                    {item.children!.map((child) => (
                      <ListItemButton
                        key={child.path}
                        onClick={() => handleNavClick(child)}
                        selected={location.pathname === child.path}
                        sx={{ borderRadius: 1, mb: 0.5 }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={child.label}
                          primaryTypographyProps={{ fontSize: '0.8125rem' }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      <Divider sx={{ borderColor: preferences.themePreset === 'classic' ? 'rgba(255,255,255,0.1)' : undefined }} />

      <Box sx={{ p: isExpanded ? 2 : 1 }}>
        {(account || user) && isExpanded && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 2,
              p: 1,
              borderRadius: 2,
              backgroundColor: preferences.themePreset === 'classic' 
                ? 'rgba(255,255,255,0.05)'
                : alpha(theme.palette.primary.main, 0.05),
            }}
          >
            <Avatar sx={{ 
              width: 32, 
              height: 32, 
              mr: 1.5,
              backgroundColor: preferences.themePreset === 'classic' ? '#3099c5' : undefined,
              color: preferences.themePreset === 'classic' ? '#ffffff' : undefined,
            }}>
              {account?.name?.[0] || user?.name?.[0] || account?.username?.[0] || user?.email?.[0]}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant="body2" 
                noWrap 
                fontWeight={600}
                sx={{ color: preferences.themePreset === 'classic' ? '#ffffff' : undefined }}
              >
                {account?.name || user?.name || account?.username || user?.email}
              </Typography>
              <Typography 
                variant="caption" 
                noWrap
                sx={{ color: preferences.themePreset === 'classic' ? '#bdbdbd' : 'text.secondary' }}
              >
                {account?.username || user?.email}
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: isExpanded ? 'row' : 'column', gap: 1, justifyContent: 'center' }}>
          <Tooltip title="Settings">
            <IconButton
              size="small"
              onClick={() => navigate('/settings')}
              sx={{ color: preferences.themePreset === 'classic' ? '#ffffff' : undefined }}
            >
              <Settings />
            </IconButton>
          </Tooltip>

          <Tooltip title="Logout">
            <IconButton 
              onClick={handleLogout} 
              size="small" 
              sx={{ color: preferences.themePreset === 'classic' ? '#ff4747' : theme.palette.error.main }}
            >
              <Logout />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <AppBar
        position="fixed"
        sx={{
          display: { sm: 'none' },
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            IT Control Panel
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{
          width: { sm: drawerWidth },
          flexShrink: { sm: 0 },
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: preferences.sidebarWidth,
              borderRadius: 0,  // No rounding on mobile drawer
            },
          }}
        >
          {drawerContent}
        </Drawer>

        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRadius: isMini ? '0 16px 16px 0' : 0,  // Round right corners only when collapsed
              transition: theme.transitions.create(['width', 'border-radius'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
              border: 'none',
              boxShadow: theme.palette.mode === 'dark'
                ? '2px 0 8px rgba(0,0,0,0.3)'
                : '2px 0 8px rgba(0,0,0,0.05)',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          overflow: 'hidden',
          transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar sx={{ display: { sm: 'none' } }} />
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default SidebarLayout;